"use client";

import { useMemo } from "react";

import {
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
} from "@nextui-org/react";
import { Key } from "@react-types/shared";

import IconArrow from "@/icons/ui/navigation/arrow-down.svg";
import { useCurrencyStore } from "@/store/currency.store";
import { Button } from "@/ui/Button";
import { cn } from "@/utils/cn";

interface Props {
  className?: string;
  classNames?: {
    arrow?: string;
    btn?: string;
  };
  arrowColor?: "red" | "gray-1200";
  isOnlySymbol?: boolean;
  showListSymbol?: boolean;
  variant?: "transparent" | "bordered";
}

export const CurrencyChanger = ({
  isOnlySymbol = false,
  className,
  classNames,
  variant = "bordered",
}: Props) => {
  const { currency, currencyList, setCurrency } = useCurrencyStore();

  const handleSelectionChange = (key: Key) => {
    setCurrency(String(key));
  };

  // Variant-based styling
  const getButtonClasses = () => {
    const baseClasses = "min-w-12 h-auto px-6 py-3 text-sm font-medium";

    if (variant === "transparent") {
      return cn(
        baseClasses,
        "bg-transparent border-0 shadow-none",
        classNames?.btn,
      );
    }

    return cn(baseClasses, "bg-white border border-gray-100", classNames?.btn);
  };

  const getDropdownClasses = useMemo(
    () => ({
      content:
        "border border-gray-100 bg-white shadow-none rounded-sm p-0 overflow-hidden min-w-[60px]",
    }),
    [],
  );

  const getItemClasses = () => ({
    base: cn(
      "px-6 py-4 cursor-pointer outline-none transition-all duration-200 rounded-none [&:not(:last-child)]:divider",
      "data-[hover=true]:opacity-60 data-[hover=true]:bg-transparent",
      "data-[selectable=true]:focus:bg-transparent",
    ),
  });

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();
  };

  return (
    <Dropdown
      placement="bottom"
      classNames={getDropdownClasses}
      shouldBlockScroll={false}
    >
      <DropdownTrigger>
        <Button
          variant="light"
          className={getButtonClasses()}
          onClick={handleClick}
          endContent={
            <IconArrow
              className={cn(
                "w-4 h-4 transition-transform shrink-0",
                classNames?.arrow || "text-inherit",
              )}
            />
          }
        >
          <div className="flex items-center gap-2">
            <span>{isOnlySymbol ? currency.symbol : currency.label}</span>
          </div>
        </Button>
      </DropdownTrigger>

      <DropdownMenu
        className={"p-0"}
        aria-label="Currency selection"
        onAction={handleSelectionChange}
        itemClasses={getItemClasses()}
      >
        {currencyList.map((item) => {
          return (
            <DropdownItem
              key={item.value}
              classNames={{
                title: "text-ellipsis overflow-hidden text-sm leading-tight",
              }}
            >
              <div className="flex items-center justify-center gap-2">
                {isOnlySymbol ? (
                  <span className="text-md">{item.symbol}</span>
                ) : (
                  <span className="text-sm">{item.label}</span>
                )}
              </div>
            </DropdownItem>
          );
        })}
      </DropdownMenu>
    </Dropdown>
  );
};
